// Suite registry. All supported cryptographic algorithms are registered here at
// module load. Provides both untyped lookups (for deserialization / wire-protocol
// negotiation where names arrive as strings) and BasicCrypto, a typed facade
// with compile-time-checked algorithm names for use by application code.
import { ed25519, mlDsa65, ed25519_mlDsa65 } from './signing.js';
import { x25519Hkdf, mlKem768, x25519Hkdf_mlKem768 } from './kem.js';
import { chacha20Poly1305 } from './aead.js';
import { hkdfSha256 } from './hkdf.js';
import { sha256, blake3 } from './hashing.js';
// Algorithm name constants (single source of truth for wire labels)
export const HASH_SHA256 = 'sha-256';
export const HASH_BLAKE3 = 'blake3';
export const SIGNING_ED25519 = 'ed25519';
export const SIGNING_ML_DSA_65 = 'ml-dsa-65';
export const SIGNING_ED25519_ML_DSA_65 = 'ed25519+ml-dsa-65';
export const KEM_X25519_HKDF = 'x25519-hkdf';
export const KEM_ML_KEM_768 = 'ml-kem-768';
export const KEM_X25519_HKDF_ML_KEM_768 = 'x25519-hkdf+ml-kem-768';
export const AEAD_CHACHA20_POLY1305 = 'chacha20-poly1305';
export const KDF_HKDF_SHA256 = 'hkdf-sha256';
// Registry maps
const signingSuites = new Map();
const kemSuites = new Map();
const aeadSuites = new Map();
const kdfSuites = new Map();
const hashSuites = new Map();
function registerSigning(s) { signingSuites.set(s.name, s); }
function registerKem(s) { kemSuites.set(s.name, s); }
function registerAead(s) { aeadSuites.set(s.name, s); }
function registerKdf(s) { kdfSuites.set(s.name, s); }
function registerHash(s) { hashSuites.set(s.name, s); }
registerSigning(ed25519);
registerSigning(mlDsa65);
registerSigning(ed25519_mlDsa65);
registerKem(x25519Hkdf);
registerKem(mlKem768);
registerKem(x25519Hkdf_mlKem768);
registerAead(chacha20Poly1305);
registerKdf(hkdfSha256);
registerHash(sha256);
registerHash(blake3);
// Untyped lookups for deserialization / wire-protocol negotiation
export function getSigningSuite(name) { return signingSuites.get(name); }
export function getKemSuite(name) { return kemSuites.get(name); }
export function getAeadSuite(name) { return aeadSuites.get(name); }
export function getKdfSuite(name) { return kdfSuites.get(name); }
export function getHashSuite(name) { return hashSuites.get(name); }
function requireSuite(map, category, name) {
    const suite = map.get(name);
    if (suite === undefined)
        throw new Error(`${category} suite '${name}' not registered`);
    return suite;
}
export function createBasicCrypto() {
    return {
        hash: (name) => requireSuite(hashSuites, 'hash', name),
        signing: (name) => requireSuite(signingSuites, 'signing', name),
        kem: (name) => requireSuite(kemSuites, 'kem', name),
        aead: (name) => requireSuite(aeadSuites, 'aead', name),
        kdf: (name) => requireSuite(kdfSuites, 'kdf', name),
    };
}
